import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Loader2, Send, Sparkles } from 'lucide-react'

import { ApiError, api } from '@/lib/api'
import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Textarea } from '@/components/ui/textarea'
import { useAuthStore } from '@/stores/auth'
import {
  AI_ASSISTANT_MAX_HISTORY,
  type AiAssistantChatBody,
  type AiAssistantChatResponse,
  type AiAssistantPageContext,
} from '@sdr-agend/admin-schemas'

interface ChatTurn {
  role: 'user' | 'assistant'
  content: string
}

interface AiAssistantWidgetProps {
  /** `null` skips rendering — used while the host page's own data hasn't loaded yet. */
  pageContext: AiAssistantPageContext | null
}

const MIN_QUESTION_LENGTH = 3

function formatResetTime(resetAt: string): string {
  const d = new Date(resetAt)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
}

/**
 * Floating "Ask AI" chat widget (2026-08-03, user request) — mounted once
 * per page with that page's `pageContext` pointer. RBAC-gates itself
 * (admin/manager only) so pages don't need to duplicate the role check.
 *
 * History is client-only/ephemeral (Article IV scope decision): it resets
 * whenever the widget unmounts (page navigation) and is replayed to the
 * backend on every question, capped at `AI_ASSISTANT_MAX_HISTORY` turns.
 */
export function AiAssistantWidget({ pageContext }: AiAssistantWidgetProps): React.ReactElement | null {
  const role = useAuthStore((s) => s.user?.role)
  const canUse = role === 'admin' || role === 'manager'

  const [open, setOpen] = useState(false)
  const [question, setQuestion] = useState('')
  const [turns, setTurns] = useState<ChatTurn[]>([])
  const [remaining, setRemaining] = useState<number | null>(null)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  const chatMutation = useMutation({
    mutationFn: async (q: string) => {
      if (pageContext === null) throw new Error('pageContext ausente')
      const body: AiAssistantChatBody = {
        pageContext,
        question: q,
        history: turns.slice(-AI_ASSISTANT_MAX_HISTORY),
      }
      return api<AiAssistantChatResponse>('/admin/ai-assistant/chat', {
        method: 'POST',
        body: JSON.stringify(body),
      })
    },
    onSuccess: (res, q) => {
      setTurns((prev) => [
        ...prev,
        { role: 'user', content: q },
        { role: 'assistant', content: res.answer },
      ])
      setRemaining(res.remainingQuestionsToday)
      setErrorMsg(null)
      setQuestion('')
    },
    onError: (err: Error) => {
      if (err instanceof ApiError && err.status === 429) {
        const body = err.bodyJson as { error?: { resetAt?: string } } | null
        const resetAt = body?.error?.resetAt
        const resetLabel = resetAt !== undefined ? formatResetTime(resetAt) : ''
        setErrorMsg(
          resetLabel !== ''
            ? `Limite de perguntas do dia atingido. Libera de novo às ${resetLabel}.`
            : 'Limite de perguntas do dia atingido.',
        )
        setRemaining(0)
        return
      }
      if (err instanceof ApiError && err.status === 404) {
        setErrorMsg('Não encontrei os dados desta página — ela pode ter sido removida ou alterada.')
        return
      }
      if (err instanceof ApiError && err.status === 503) {
        setErrorMsg('O assistente de IA está indisponível no momento. Tente novamente mais tarde.')
        return
      }
      setErrorMsg('Não consegui responder agora. Tente novamente.')
    },
  })

  if (!canUse) return null

  function handleSend(): void {
    const trimmed = question.trim()
    if (trimmed.length < MIN_QUESTION_LENGTH || chatMutation.isPending || remaining === 0) return
    chatMutation.mutate(trimmed)
  }

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-40 h-12 w-12 rounded-full shadow-lg"
        size="icon"
        aria-label="Abrir assistente de IA"
      >
        <Sparkles className="h-5 w-5" aria-hidden="true" />
      </Button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="flex w-full flex-col sm:max-w-md">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Sparkles className="h-4 w-4" aria-hidden="true" />
              Assistente de IA
            </SheetTitle>
            <SheetDescription>
              Pergunte sobre os números da IA ou peça sugestões de melhoria para esta página.
              {remaining !== null ? ` ${remaining} pergunta(s) restante(s) hoje.` : ''}
            </SheetDescription>
          </SheetHeader>

          <div className="flex-1 space-y-3 overflow-y-auto py-4">
            {turns.length === 0 && (
              <p className="text-sm text-muted-foreground">
                Ex.: &quot;Como estão os números da IA essa semana?&quot; ou &quot;O que podemos
                melhorar aqui?&quot;
              </p>
            )}
            {turns.map((t, i) => (
              <div
                key={i}
                className={
                  t.role === 'user'
                    ? 'ml-6 rounded-lg bg-primary px-3 py-2 text-sm text-primary-foreground'
                    : 'mr-6 rounded-lg bg-muted px-3 py-2 text-sm'
                }
              >
                {t.content}
              </div>
            ))}
            {chatMutation.isPending && (
              <div className="mr-6 flex items-center gap-2 rounded-lg bg-muted px-3 py-2 text-sm text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
                Pensando...
              </div>
            )}
            {errorMsg !== null && (
              <p role="alert" className="text-sm text-destructive">
                {errorMsg}
              </p>
            )}
          </div>

          <div className="flex items-end gap-2 border-t pt-3">
            <Textarea
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  handleSend()
                }
              }}
              placeholder="Digite sua pergunta..."
              disabled={pageContext === null || remaining === 0 || chatMutation.isPending}
              className="min-h-[44px] flex-1 resize-none"
              rows={1}
            />
            <Button
              onClick={handleSend}
              disabled={
                pageContext === null ||
                question.trim().length < MIN_QUESTION_LENGTH ||
                remaining === 0 ||
                chatMutation.isPending
              }
              size="icon"
              aria-label="Enviar pergunta"
            >
              <Send className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  )
}
